import * as React from 'react';
import {
  LedgerNavigationLayout,
  LedgerNavigationHeader,
  LedgerNavigationLinks,
  LedgerNavigationLink,
  LedgerNavigationCategoryHeader,
  EntryCount,
  LedgerNameWrapper,
} from './LedgerSidebar.styles';
import { useLedgerList } from 'general-ledger/fast-gl/hooks/useLedgerList';
import { LedgerSidebarSkeleton } from './LedgerSidebarSkeleton';
import { useGeneralLedger } from 'general-ledger/fast-gl/hooks/useGeneralLedger';
import { ErrorMessage } from '../../../../client-web-app/components';

type LedgerSidebarProps = {
  onItemClick: (index: number) => void;
};

const LedgerSidebar = ({ onItemClick }: LedgerSidebarProps): React.ReactElement => {
  const { ledgers, loading, error } = useLedgerList();
  const { hiddenCategories, updateHiddenCategories, search } = useGeneralLedger();
  const QUALITY_ISSUES_SUMMARY = 'Quality Issues Summary';

  const handleLedgerCategoryClick = (index: number, ledgerCategoryId: string) => {
    if (!updateHiddenCategories) return;
    onItemClick(index);
    hiddenCategories.includes(ledgerCategoryId) &&
      updateHiddenCategories(hiddenCategories.filter(id => id !== ledgerCategoryId));
  };

  if (error) {
    return <ErrorMessage>There was an error loading ledgers. Please refresh.</ErrorMessage>;
  }

  return (
    <LedgerNavigationLayout>
      <LedgerNavigationHeader>Ledgers & Accounts</LedgerNavigationHeader>
      <LedgerNavigationLinks>
        {!loading ? (
          ledgers.map((ledger, index) => {
            const isQualityIssuesAndSearch = ledger.name === QUALITY_ISSUES_SUMMARY && search;
            const isFirstOfGroup =
              index === 0 || ledgers[index - 1].category.id !== ledger.category.id;
            const isHidden =
              hiddenCategories.includes(ledger.category.id) || isQualityIssuesAndSearch;
            return (
              <div key={ledger.id}>
                {isFirstOfGroup && !isQualityIssuesAndSearch ? (
                  <LedgerNavigationCategoryHeader
                    index={index}
                    scrollToItem={(index: number) => {
                      handleLedgerCategoryClick(index, ledger.category.id);
                    }}
                  >
                    {ledger.category.name}
                  </LedgerNavigationCategoryHeader>
                ) : null}
                {isHidden ? null : (
                  <LedgerNavigationLink index={index} scrollToItem={onItemClick}>
                    <LedgerNameWrapper>
                      {ledger.name}
                      <div>
                        <EntryCount>{ledger.dataEntries.pageInfo.total}</EntryCount>
                      </div>
                    </LedgerNameWrapper>
                  </LedgerNavigationLink>
                )}
              </div>
            );
          })
        ) : (
          <LedgerSidebarSkeleton />
        )}
      </LedgerNavigationLinks>
    </LedgerNavigationLayout>
  );
};

export default React.memo(LedgerSidebar);
